import { useCallback, useEffect, useRef, useState } from 'react';

import type { Task, TaskStatus } from '@nedelnik/shared';

/**
 * Пауза при закрытии задачи (3.1.8).
 *
 * Отмеченная готовой задача мгновенно уезжала в «Готово» — и случайное
 * нажатие проходило незамеченным: задача просто пропадала из виду. Теперь пару
 * секунд она остаётся на месте, зачёркнутая, и только потом уходит вниз.
 *
 * Удержание живёт в интерфейсе, а не в данных: сама задача закрывается сразу,
 * и всё, что от неё зависит — счётчики, прогресс проекта, блокировки, —
 * обновляется без задержки. Откладывается только то, где строка нарисована.
 * Иначе пришлось бы держать «почти закрытую» задачу во всём приложении, включая
 * синхронизацию с другими устройствами.
 *
 * Снятая за эти секунды галочка — отмена, а не новое действие: задача
 * возвращается в тот статус, в котором стоит на экране, и остаётся на своём
 * месте. Раньше удержание снималось сразу, а правка доезжала до данных чуть
 * позже, и в этот промежуток задача на миг показывалась закрытой — уезжала в
 * «Готово» и возвращалась уже в бэклог, часто в другое место (замечание
 * владельца, 2026-10-11). Поэтому после отмены удержание живёт, пока данные
 * его не догонят.
 */

/** Сколько задача остаётся на месте после закрытия. */
export const HOLD_MS = 2000;

/**
 * Сколько ждать, пока отмена доедет до данных. Обычно это миллисекунды; срок —
 * на случай, когда правка не доехала вовсе: показывать задачу не там, где она
 * есть, дольше нельзя.
 */
const REOPEN_HOLD_MS = 3000;

interface Held {
  /** Каким статусом задача показана — тот, из которого её закрыли. */
  status: TaskStatus;
  /** `closing` — закрыта и стоит на месте; `reopening` — отмена едет в данные. */
  phase: 'closing' | 'reopening';
}

export interface ClosingHold {
  /** Обёртка над отметкой: закрывает задачу и запускает удержание или отменяет его. */
  toggle: (task: Task) => void;
  /** Каким статусом показывать задачу сейчас. */
  statusOf: (task: Task) => TaskStatus;
  /** Задача закрыта только что и ещё стоит на прежнем месте. */
  isClosing: (task: Task) => boolean;
  /** Стоит ли на задаче галочка — по тому, что видит человек, а не по данным. */
  isDone: (task: Task) => boolean;
}

export interface DoneSetters {
  close: (task: Task) => void;
  /** `to` — статус, известный экрану; без него решает память устройства. */
  reopen: (task: Task, to?: TaskStatus) => void;
}

export function useClosingHold(tasks: readonly Task[], setters: DoneSetters): ClosingHold {
  const [held, setHeld] = useState<ReadonlyMap<string, Held>>(new Map());
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  // Таймеры переживают размонтирование, если их не снять: строка исчезает
  // вместе со списком, а обновление состояния приходит в мёртвый компонент.
  useEffect(() => {
    const running = timers.current;
    return () => {
      for (const timer of running.values()) clearTimeout(timer);
      running.clear();
    };
  }, []);

  const release = useCallback((taskId: string) => {
    const timer = timers.current.get(taskId);
    if (timer !== undefined) clearTimeout(timer);
    timers.current.delete(taskId);

    setHeld((was) => {
      if (!was.has(taskId)) return was;
      const next = new Map(was);
      next.delete(taskId);
      return next;
    });
  }, []);

  const hold = useCallback(
    (taskId: string, entry: Held, ms: number) => {
      const timer = timers.current.get(taskId);
      if (timer !== undefined) clearTimeout(timer);
      timers.current.set(
        taskId,
        setTimeout(() => release(taskId), ms),
      );
      setHeld((was) => new Map(was).set(taskId, entry));
    },
    [release],
  );

  // Отмена доехала: данные показывают то же, что и экран, — держать нечего.
  useEffect(() => {
    for (const [id, entry] of held) {
      if (entry.phase !== 'reopening') continue;
      const task = tasks.find((item) => item.id === id);
      if (task === undefined || task.status !== 'done') release(id);
    }
  }, [tasks, held, release]);

  const { close, reopen } = setters;

  const toggle = useCallback(
    (task: Task) => {
      const entry = held.get(task.id);

      if (entry?.phase === 'closing') {
        reopen(task, entry.status);
        hold(task.id, { status: entry.status, phase: 'reopening' }, REOPEN_HOLD_MS);
        return;
      }

      // Задача давно в «Готово»: она и должна переехать в свою группу, держать
      // её на месте незачем.
      if (entry === undefined && task.status === 'done') {
        reopen(task);
        return;
      }

      // Закрыть — в том числе ту, отмена которой ещё едет: данные у неё пока
      // «готово», и судить о прежнем статусе можно только по экрану.
      close(entry?.phase === 'reopening' ? { ...task, status: entry.status } : task);
      hold(task.id, { status: entry?.status ?? task.status, phase: 'closing' }, HOLD_MS);
    },
    [held, close, reopen, hold],
  );

  const statusOf = useCallback((task: Task) => held.get(task.id)?.status ?? task.status, [held]);
  const isClosing = useCallback((task: Task) => held.get(task.id)?.phase === 'closing', [held]);
  const isDone = useCallback(
    (task: Task) => {
      const entry = held.get(task.id);
      return entry === undefined ? task.status === 'done' : entry.phase === 'closing';
    },
    [held],
  );

  return { toggle, statusOf, isClosing, isDone };
}

/**
 * Статус, которым задачу рисовать: только что закрытая — уже «готово»
 * (зачёркнута), отменённая — прежним, даже пока данные не догнали экран.
 */
export function shownStatus(task: Task, closing: ClosingHold): TaskStatus {
  return closing.isDone(task) ? 'done' : closing.statusOf(task);
}
